import type { State } from 'src/replicant/State';
import {
  superLollipopUnlockLevel,
  superLollipopWinStreakRequiredDefault,
} from './superLollipop.ruleset';
import type { SuperLollipopUserProperties } from './superLollipop.analytics.schema';
import { dreamDriftGetPuzzlePlaying } from '../dreamDrift/dreamDrift.getters';

/**
 * Returns the configured default win streak required to activate the Super Lollipop feature.
 *
 * @param state - The application state.
 * @returns The configured default required win streak.
 */
export function getSuperLollipopWinStreakRequired(state: State): number {
  return superLollipopWinStreakRequiredDefault;
}

/**
 * Check if the super lollipop feature is available for the user
 * @param state
 * @returns
 */
export function isSuperLollipopAvailable(
  state: State,
  level?: number,
): boolean {
  // Use the level from the state if not provided
  if (!level) level = state.match2.level;

  if (level < superLollipopUnlockLevel) {
    return false;
  }

  return true;
}

/**
 * Check if super lollipop is active for the user, if the feature is available
 * and user reached the required streak
 * @param state
 * @returns
 */
export function isSuperLollipopActive(state: State): boolean {
  // Dream Drift puzzles suspend the Super Lollipop without consuming its streak.
  if (dreamDriftGetPuzzlePlaying(state)) return false;

  // The feature must be unlocked before its streak can activate it.
  if (!isSuperLollipopAvailable(state)) {
    return false;
  }

  // The required win streak activates the Super Lollipop.
  return (
    getSuperLollipopWinStreak(state) >= getSuperLollipopWinStreakRequired(state)
  );
}

/**
 * Check current super lollipop streak progress, capped at the required streak
 * @param state
 * @returns
 */
export function getSuperLollipopWinStreak(
  state: State,
  capped = false,
): number {
  const streak = state.superLollipop?.winStreak ?? 0;
  if (capped) {
    return Math.min(streak, getSuperLollipopWinStreakRequired(state));
  }
  return streak;
}

/**
 * Check if super lollipop is available for the user and see how many wins the user has after streak became active
 * @param state
 * @returns
 */
function getSuperLollipopActiveWinStreak(state: State) {
  if (!isSuperLollipopActive(state)) {
    // If super lollipop is not active, return 0
    return 0;
  } else {
    // Otherwise, return the number of wins after the streak became active
    return (
      (state.superLollipop?.winStreak ?? 0) -
      getSuperLollipopWinStreakRequired(state)
    );
  }
}

/**
 * Retrieve the number of times the super lollipop feature has been activated
 * @param state
 * @returns
 */
export function getSuperLollipopActivationCount(state: State): number {
  return state.superLollipop?.activationCount ?? 0;
}

/**
 * Retrieve the last seen timestamp for the super lollipop
 * @param state
 * @returns
 */
export function getSuperLollipopLastSeen(state: State): number {
  return state.superLollipop?.lastSeenAt ?? 0;
}

/**
 * Retrieve the Super Lollipop user properties
 * @param state
 * @returns
 */
export function getSuperLollipopUserProperties(
  state: State,
): SuperLollipopUserProperties {
  // If the feature is not available, return an empty object
  if (!isSuperLollipopAvailable(state)) {
    return {};
  }
  // Otherwise, return the user properties related to the super lollipop feature
  return {
    realtimeSuperLollipopAcquiredCount: getSuperLollipopActivationCount(state),
    realtimeSuperLollipopStreak: getSuperLollipopActiveWinStreak(state),
    realtimeIsSuperLollipopActive: isSuperLollipopActive(state),
  };
}
